'use client';

import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import type { Action, FighterInit, RoundEvent, Team } from '@cs-shot/battle-sim';
import { fighterLayout } from '@cs-shot/battle-sim';
import { cn } from '../../../utils/cn';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import { useHaptic } from '../../../hooks/useHaptic';
import { FaBolt } from '../../icons';
import { ActionMenu, type Stance } from './ActionMenu';
import { loadBattleAssets, type BattleAssets } from './battleAssets';
import { RoundLogSheet } from './RoundLogSheet';
import { sideColor } from './sideColor';
import translations from './RushMid.translations';
import type { RoundUpdate } from './useRushMidMatch';
import { useBattleCanvas, WORLD_W, WORLD_H, ACTION_BAR_RESERVE } from './useBattleCanvas';
import { useRoundTimer } from './useRoundTimer';
import { useRoundReveal } from './useRoundReveal';
import { startHeartbeat, stopHeartbeat } from './sfx';

export interface RushMidLiveBattleProps {
  update: RoundUpdate;
  onAction: (action: Action, round: number) => void;
  onFinished: (won: boolean) => void;
  replay?: boolean;
}

export const RushMidLiveBattle: FC<RushMidLiveBattleProps> = ({ update, onAction, onFinished, replay = false }) => {
  const { t } = useLocalTranslation(translations);
  const haptic = useHaptic();
  const hapticRef = useRef(haptic);
  hapticRef.current = haptic;

  const initsRef = useRef<FighterInit[]>([]);
  if (update.inits?.length) initsRef.current = update.inits;
  const inits = initsRef.current;

  const youId = update.yourFighterId ?? null;
  const yourSide = inits.find((i) => i.id === youId)?.team;
  const fighters = update.fighters;
  const byId = useMemo(() => Object.fromEntries(fighters.map((f) => [f.id, f])), [fighters]);
  const pos = useMemo(() => {
    const teamSize = { t: 0, ct: 0 };
    for (const i of inits) teamSize[i.team]++;
    return new Map(inits.map((i) => [i.id, fighterLayout(i.team, i.index, teamSize[i.team])]));
  }, [inits]);
  const you = youId ? byId[youId] : null;
  const aliveEnemies = useMemo(
    () => fighters.filter((f) => f.team !== yourSide && f.alive),
    [fighters, yourSide],
  );
  const [target, setTarget] = useState<string | null>(null);
  const targetRef = useRef<string | null>(null);
  targetRef.current = target;
  const targetIsAlly = !!target && byId[target]?.team === yourSide;
  const targetIsAllyRef = useRef(false);
  targetIsAllyRef.current = targetIsAlly;
  const [localSubmitted, setLocalSubmitted] = useState(false);
  const submitted = localSubmitted || !!update.yourActionSubmitted;
  const submittedRef = useRef(false);
  submittedRef.current = submitted;
  const [chosenAction, setChosenAction] = useState<Action | null>(null);
  const chosenActionRef = useRef<Action | null>(null);
  chosenActionRef.current = chosenAction;
  const [lastStance, setLastStance] = useState<Stance>('attack');
  const round = update.round;
  const over = update.over;

  const [logOpen, setLogOpen] = useState(false);
  const roundsLogRef = useRef(new Map<number, RoundEvent[]>());
  useEffect(() => {
    if (update.events.length) roundsLogRef.current.set(update.round, update.events);
  }, [update.round, update.events]);

  const adrenaline = useMemo(() => {
    const shields = update.events.filter(
      (e): e is Extract<RoundEvent, { type: 'shield' }> => e.type === 'shield',
    );
    if (!shields.length) return null;
    const team: Team = shields[0].id.startsWith('ct') ? 'ct' : 't';
    return { team, amount: shields[0].amount };
  }, [update.events]);

  useEffect(() => { setLocalSubmitted(false); setChosenAction(null); }, [round]);

  const actedThisRoundRef = useRef(false);
  useEffect(() => {
    if (submitted) actedThisRoundRef.current = true;
  }, [submitted]);
  const mountedRef = useRef(false);
  const [missedLastRound, setMissedLastRound] = useState(false);
  useEffect(() => {
    if (mountedRef.current) {
      setMissedLastRound(!actedThisRoundRef.current);
      actedThisRoundRef.current = false;
    }
    mountedRef.current = true;
  }, [round]);

  const commit = (a: Action) => {
    if (submittedRef.current || over) return;
    setLocalSubmitted(true);
    setChosenAction(a);
    onAction(a, update.round);
  };
  const commitRef = useRef(commit);
  commitRef.current = commit;

  const [assets, setAssets] = useState<BattleAssets | null>(null);
  const map = update.map;
  useEffect(() => {
    let ok = true;
    loadBattleAssets(map).then((a) => { if (ok) setAssets(a); });
    return () => { ok = false; };
  }, [map]);

  const updateRef = useRef<RoundUpdate | null>(update);
  updateRef.current = update;
  const youIdRef = useRef(youId);
  youIdRef.current = youId;

  const missLabels = useMemo(
    () => ({ miss: t('canvasMiss'), evade: t('canvasMissEvade'), blind: t('canvasMissBlind') }),
    [t],
  );
  const missLabelsRef = useRef(missLabels);
  missLabelsRef.current = missLabels;

  const { canvasRef, engineRef, field } = useBattleCanvas(assets, replay, { updateRef, initsRef, youIdRef, targetRef, chosenActionRef, hapticRef, missLabelsRef });

  const { remain, replayLocked } = useRoundTimer(engineRef, round, over, () =>
    commitRef.current(
      targetIsAllyRef.current
        ? { kind: 'smoke', smokeTargetId: targetRef.current }
        : { kind: 'shoot', stance: 'attack', shootTargetId: targetRef.current },
    ),
  );

  const youHp = you?.hp;
  const youAlive = you?.alive;
  const [settledHp, setSettledHp] = useState<number | null>(null);
  const [settledAlive, setSettledAlive] = useState(true);
  useEffect(() => {
    if (replayLocked || youHp == null) return;
    setSettledHp(youHp);
    setSettledAlive(!!youAlive);
  }, [replayLocked, youHp, youAlive]);
  const heartbeatOn =
    !replay && !over && settledAlive && settledHp !== null && settledHp > 0 && settledHp <= 15;
  useEffect(() => {
    if (heartbeatOn) void startHeartbeat();
    else stopHeartbeat();
  }, [heartbeatOn]);
  useEffect(() => () => stopHeartbeat(), []);

  useEffect(() => {
    if (replayLocked || replay) return;
    setTarget((prev) => {
      // WHY: без смока цель-союзник → сервер молча стреляет в случайного врага; сбрасывать союзника, как только смок потрачен
      const allyNoSmoke = !!prev && byId[prev]?.team === yourSide && !you?.hasSmoke;
      if (prev && !allyNoSmoke && fighters.some((f) => f.id === prev && f.alive)) return prev;
      if (!aliveEnemies.length) return null;
      const yp = youId ? pos.get(youId) : undefined;
      let best = aliveEnemies[0].id; let bestD = Infinity;
      for (const en of aliveEnemies) {
        const ep = pos.get(en.id);
        if (!ep || !yp) continue;
        const d = Math.hypot(ep.targetX - yp.targetX, ep.y - yp.y);
        if (d < bestD) { bestD = d; best = en.id; }
      }
      return best;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayLocked, aliveEnemies]);

  const showResult = useRoundReveal(engineRef, over, round, {
    won: !!you?.alive,
    winner: update.winner ?? null,
    yourSide,
    onFinished,
  });

  const onCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (over || submitted || replayLocked || !you?.alive) return;
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    // WHY: хит-тест повторяет stage-трансформ движка — высота rect.height - ACTION_BAR_RESERVE, иначе тапы промахиваются по
    // нижнему ряду
    const availH = Math.max(1, rect.height - ACTION_BAR_RESERVE);
    const s = Math.min(rect.width / WORLD_W, availH / WORLD_H);
    const ox = (rect.width - WORLD_W * s) / 2;
    const oy = (availH - WORLD_H * s) / 2;
    const wx = (e.clientX - rect.left - ox) / s;
    const wy = (e.clientY - rect.top - oy) / s;
    const canFriendly = !!you.hasSmoke;
    let best: string | null = null; let bestD = Infinity;
    for (const f of fighters) {
      if (!f.alive) continue;
      if (f.id === youId) continue;
      if (!canFriendly && f.team === yourSide) continue;
      const p = pos.get(f.id);
      if (!p) continue;
      const d = Math.hypot(p.targetX - wx, p.y - 130 - wy);
      if (d < bestD) { bestD = d; best = f.id; }
    }
    if (best && bestD < 260) setTarget(best);
  };

  const canAct = !over && !submitted && !replayLocked && !!you?.alive;

  return (
    // WHY: бой всегда dir="ltr" — canvas рисует свою систему координат, зеркалирование DOM сдвинет оверлеи с картинки
    <div dir="ltr" className="absolute inset-0 overflow-hidden bg-black">
      <canvas ref={canvasRef} onClick={onCanvasClick} className="h-full w-full" style={{ display: 'block' }} />

      {!assets && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-accent-blue" />
        </div>
      )}

      {over && showResult && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="rounded-xl bg-black/75 px-7 py-4 text-3xl font-extrabold tracking-wide">
            {update.winner ? (
              <>
                <span className={sideColor(update.winner, 'text')}>
                  {update.winner === 't' ? 'T' : 'CT'}
                </span>
                <span className="text-white"> {t('sideWins')}</span>
              </>
            ) : (
              <span className="text-white">{t('matchDraw')}</span>
            )}
          </span>
        </div>
      )}

      {!over && !replay && (
        <div
          className="pointer-events-none absolute inset-x-0 flex flex-col items-center gap-1.5"
          style={{ top: 'calc(44px + var(--tg-viewport-content-safe-area-inset-top, 0px) + var(--tg-viewport-safe-area-inset-top, 0px))' }}
        >

          <span className="rounded-full bg-black/55 px-3 py-0.5 text-xs font-bold uppercase tracking-widest text-label-secondary backdrop-blur">
            {t('roundN', { round: replayLocked ? round : round + 1 })}
          </span>
          {replayLocked ? (
            <span className="rounded-full bg-black/60 px-4 py-1.5 text-sm font-semibold text-label-secondary backdrop-blur">
              {t('watchingReplay')}
            </span>
          ) : (
            <span
              className={cn(
                'flex h-16 min-w-16 items-center justify-center rounded-2xl bg-black/55 px-4 text-4xl font-extrabold tabular-nums backdrop-blur',
                remain <= 2 ? 'text-accent-red' : 'text-white',
              )}
            >
              {remain}
            </span>
          )}
          {roundsLogRef.current.size > 0 && (
            <button
              onClick={() => setLogOpen(true)}
              className="pointer-events-auto rounded-full bg-black/55 px-3 py-1 text-xs font-bold uppercase tracking-widest text-label-secondary backdrop-blur"
            >
              {t('roundLog')}
            </button>
          )}
        </div>
      )}

      {!over && (adrenaline || (!replay && missedLastRound && !replayLocked && !submitted && you?.alive)) && (
        <div
          className="pointer-events-none absolute inset-x-0 flex flex-col items-center gap-1.5"
          style={{ top: 'calc(150px + var(--tg-viewport-content-safe-area-inset-top, 0px) + var(--tg-viewport-safe-area-inset-top, 0px))' }}
        >
          {adrenaline && (
            <span className="flex items-center gap-1.5 rounded-full bg-black/65 px-3 py-1 text-sm font-semibold backdrop-blur">
              <FaBolt size={14} className="text-side-t" />
              <span className={adrenaline.team === 't' ? 'text-side-t' : 'text-accent-blue'}>{adrenaline.team === 't' ? 'T' : 'CT'}</span>
              <span className="text-label-secondary">{t('adrenaline')}</span>
              <span className="text-side-t">+{adrenaline.amount}</span>
            </span>
          )}

          {!replay && missedLastRound && !replayLocked && !submitted && you?.alive && (
            <span className="rounded-full bg-accent-red/20 px-3 py-1 text-sm font-semibold text-accent-red backdrop-blur">
              {t('afkWarn')}
            </span>
          )}
        </div>
      )}

      {!replay && (
        // WHY: прижато к физическому низу, safe-area игнорируется намеренно — бар должен совпасть с игровым полем, не с чёрными
        // полями
        <div
          className="pointer-events-none absolute bottom-0 flex flex-col items-center"
          style={field.width ? { left: field.x, width: field.width } : { left: 0, right: 0 }}
        >

          {(you?.alive || replayLocked) && (
            <div className="pointer-events-auto w-full">
              <ActionMenu
                canAct={canAct}
                hasGrenade={!!you?.hasGrenade}
                hasSmoke={!!you?.hasSmoke}
                hasFlash={!!you?.hasFlash}
                hasMedkit={!!you?.hasMedkit}
                targetIsAlly={targetIsAlly}
                stance={lastStance}
                onStance={(stance) => { setLastStance(stance); commit({ kind: 'shoot', stance, shootTargetId: targetRef.current }); }}
                onKnife={() => commit({ kind: 'knife', shootTargetId: targetRef.current })}
                onGrenade={() => commit({ kind: 'grenade', grenadeTargetId: targetRef.current })}
                onSmoke={() => commit({ kind: 'smoke', smokeTargetId: targetRef.current })}
                onFlash={() => commit({ kind: 'flash', flashTargetId: targetRef.current })}
                onMedkit={() => commit({ kind: 'medkit' })}
              />
            </div>
          )}
        </div>
      )}

      {!replay && (
        <RoundLogSheet
          isOpen={logOpen}
          onClose={() => setLogOpen(false)}
          inits={inits}
          rounds={[...roundsLogRef.current.entries()].sort((a, b) => a[0] - b[0]).map(([, e]) => e)}
        />
      )}
    </div>
  );
};

export default RushMidLiveBattle;
